import { Component, HostBinding, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, concatMap, distinctUntilChanged, from, map, withLatestFrom } from 'rxjs';
import { UserDataShared } from '../../../models/interface/userData.interface';
import { ChatState } from '../../../sharedServices/chat-variant';
import { ChatVariantService } from '../../../sharedServices/chat-variant.service';
import { FreshchatService } from '../../../sharedServices/freshchat.service';
import { MU_CHAT_CONFIG, MU_CHAT_DEFAULT_HEIGHT, MU_CHAT_DEFAULT_WIDTH } from '../../../sharedServices/mu-chat.config';
import { UserDataService } from '../../../sharedServices/user-data.service';

/** Configured size in px, or the default when the value is not a positive finite number. */
const toPx = (value: number, fallback: number): string => (Number.isFinite(value) && value > 0 ? value : fallback) + 'px';

/**
 * Starts Freshchat while the state asks for it and destroys it as soon as the state moves away,
 * so Freshchat and ARENA are never alive together. Freshchat draws its own markup outside the host.
 */
export function bindFreshchat(
  state$: Observable<ChatState>,
  userData$: Observable<UserDataShared | null>,
  freshchat: FreshchatService
): Observable<void> {
  let started = false;
  return state$.pipe(
    map(state => (state.kind === 'freshchat' ? state : null)),
    distinctUntilChanged((a, b) => a?.opened === b?.opened && !a === !b),
    withLatestFrom(userData$),
    concatMap(([state, userData]) =>
      from(
        (async () => {
          if (!state) {
            if (started) {
              freshchat.destroy();
              started = false;
            }
            return;
          }
          if (!(await freshchat.load())) {
            return;
          }
          freshchat.init(userData?.id ? userData : null);
          started = true;
          if (state.opened) {
            freshchat.open();
          }
        })()
      )
    )
  );
}

/**
 * Chat host: renders the single surface chosen by the chat experiment
 * (anonymous launcher, country selector or ARENA iframe) and drives Freshchat.
 */
@Component({
  selector: 'app-mu-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.sass'],
})
export class MuChatComponent {
  private readonly config = inject(MU_CHAT_CONFIG);
  readonly chat = inject(ChatVariantService);
  readonly state$: Observable<ChatState> = this.chat.state$;
  @HostBinding('style.--mu-chat-width') readonly width = toPx(this.config.width, MU_CHAT_DEFAULT_WIDTH);
  @HostBinding('style.--mu-chat-height') readonly height = toPx(this.config.height, MU_CHAT_DEFAULT_HEIGHT);

  constructor() {
    bindFreshchat(this.state$, inject(UserDataService).getUserData(), inject(FreshchatService)).pipe(takeUntilDestroyed()).subscribe();
  }
}
